import React, { useState, useEffect, useRef } from 'react';
import '../styles/FacturasAttachments.css';

interface Attachment {
  attachment_id: string;
  filename: string;
  file_type?: string;
  content_type?: string;
  file_size: number;
  upload_date?: string;
  uploaded_at?: string;
  url?: string;
  s3_key?: string;
  status?: string;
}

export interface FacturasAttachmentsProps {
  facturaId: string;
  apiBaseUrl?: string;
  maxFileSize?: number; // in bytes, default 50MB
  allowedFileTypes?: string[];
  maxAttachments?: number;
  initialOpen?: boolean;
  onAttachmentAdded?: (attachment: Attachment) => void;
  onAttachmentDeleted?: (id: string) => void;
  onError?: (error: string) => void;
}

export const FacturasAttachments: React.FC<FacturasAttachmentsProps> = ({
  facturaId,
  apiBaseUrl = 'https://doc-ia-processor.sindicalia.com.ar',
  maxFileSize = 50 * 1024 * 1024, // 50MB
  allowedFileTypes = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'],
  maxAttachments = 20,
  initialOpen = false,
  onAttachmentAdded,
  onAttachmentDeleted,
  onError,
}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingButton, setIsDraggingButton] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [viewerFile, setViewerFile] = useState<{ attachment: Attachment; url: string } | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loadingText, setLoadingText] = useState(false);
  const [fileConflicts, setFileConflicts] = useState<Array<{ file: File; existing: Attachment }>>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (facturaId) {
      fetchAttachments();
    }
  }, [facturaId]);

  const fetchAttachments = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/attachments/facturas/${encodeURIComponent(facturaId)}`);
      if (!response.ok) throw new Error('Error al cargar adjuntos');
      const responseData = await response.json();

      // Extraer attachments de la respuesta (maneja tanto {data: {attachments: [...]}} como {attachments: [...]} directo)
      const data = responseData.data || responseData;
      const attachmentsList = data.attachments || [];

      // Ordenar alfabéticamente por nombre de archivo
      attachmentsList.sort((a: Attachment, b: Attachment) =>
        a.filename.localeCompare(b.filename, 'es', { sensitivity: 'base' })
      );

      setAttachments(attachmentsList);
    } catch (err) {
      const errorMsg = 'No se pudieron cargar los adjuntos';
      setError(errorMsg);
      onError?.(errorMsg);
    }
  };

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const getFileIcon = (filename: string): JSX.Element => {
    const ext = getFileExtension(filename);

    // PDF - Rojo
    if (ext === 'pdf') {
      return (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="8" y="4" width="32" height="40" rx="2" fill="#E74C3C" />
          <path d="M14 14h20M14 20h20M14 26h14" stroke="white" strokeWidth="2" strokeLinecap="round" />
          <text x="24" y="39" fontFamily="Arial, sans-serif" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle">PDF</text>
        </svg>
      );
    }

    // Imágenes - Verde
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].indexOf(ext) !== -1) {
      return (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="8" y="4" width="32" height="40" rx="2" fill="#27AE60" />
          <circle cx="18" cy="16" r="3" fill="white" opacity="0.8" />
          <path d="M8 32 L16 24 L24 32 L32 24 L40 32 V42 C40 43.1 39.1 44 38 44 H10 C8.9 44 8 43.1 8 42 V32Z" fill="white" opacity="0.6" />
        </svg>
      );
    }

    // Word - Azul
    if (['doc', 'docx'].indexOf(ext) !== -1) {
      return (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="8" y="4" width="32" height="40" rx="2" fill="#2980B9" />
          <path d="M14 12h20M14 18h20M14 24h20" stroke="white" strokeWidth="2" strokeLinecap="round" />
          <text x="24" y="38" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="bold" fill="white" textAnchor="middle">DOC</text>
        </svg>
      );
    }

    // Excel - Verde oscuro
    if (['xls', 'xlsx', 'csv'].indexOf(ext) !== -1) {
      return (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="8" y="4" width="32" height="40" rx="2" fill="#16A085" />
          <path d="M14 14h20M14 20h20M14 26h20M20 10v20M28 10v20" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <text x="24" y="39" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="bold" fill="white" textAnchor="middle">XLS</text>
        </svg>
      );
    }

    // Texto - Gris
    if (['txt', 'md'].indexOf(ext) !== -1) {
      return (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="8" y="4" width="32" height="40" rx="2" fill="#7F8C8D" />
          <path d="M14 14h20M14 20h20M14 26h16" stroke="white" strokeWidth="2" strokeLinecap="round" />
          <text x="24" y="38" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="bold" fill="white" textAnchor="middle">TXT</text>
        </svg>
      );
    }

    // Comprimidos - Naranja
    if (['zip', 'rar', '7z'].indexOf(ext) !== -1) {
      return (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="8" y="4" width="32" height="40" rx="2" fill="#E67E22" />
          <path d="M22 8h4M22 12h4M22 16h4M22 20h4" stroke="white" strokeWidth="2" />
          <rect x="20" y="24" width="8" height="6" rx="1" fill="white" opacity="0.8" />
          <text x="24" y="39" fontFamily="Arial, sans-serif" fontSize="9" fontWeight="bold" fill="white" textAnchor="middle">ZIP</text>
        </svg>
      );
    }

    // Default - Púrpura
    return (
      <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="8" y="4" width="32" height="40" rx="2" fill="#667EEA" />
        <path d="M14 14h20M14 20h20M14 26h16" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
        <circle cx="24" cy="24" r="8" fill="white" opacity="0.3" />
        <path d="M24 20v8M20 24h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const validateFile = (file: File): string | null => {
    const ext = getFileExtension(file.name);

    if (allowedFileTypes.indexOf(ext) === -1) {
      return `Tipo de archivo no permitido. Permitidos: ${allowedFileTypes.join(', ')}`;
    }

    if (file.size > maxFileSize) {
      return `El archivo es muy grande. Máximo: ${formatFileSize(maxFileSize)}`;
    }

    if (attachments.length >= maxAttachments) {
      return `Máximo ${maxAttachments} adjuntos permitidos`;
    }

    return null;
  };

  const uploadFile = async (file: File, isMultiple: boolean = false) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      onError?.(validationError);
      return false;
    }

    if (!isMultiple) {
      setIsLoading(true);
      setUploadProgress(0);
    }

    setError(null);

    try {
      setUploadingFiles(prev => [...prev, file.name]);

      const presignResponse = await fetch(`${apiBaseUrl}/attachments/facturas/${encodeURIComponent(facturaId)}/upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          file_size: file.size,
        }),
      });

      console.log({presignResponse})

      if (!presignResponse.ok) throw new Error('Error al obtener URL de carga');
      const presignData = await presignResponse.json();

      // Extraer datos de la respuesta (maneja tanto {data: {...}} como {...} directo)
      const responseData = presignData.data || presignData;
      const { upload_url, attachment_id, upload_headers } = responseData;

      // Step 2: Upload to S3
      const uploadHeaders: Record<string, string> = {
        ...(upload_headers || {}),
      };

      // Si no hay headers específicos, usar Content-Type del archivo
      if (!upload_headers || !upload_headers['Content-Type']) {
        uploadHeaders['Content-Type'] = file.type || 'application/octet-stream';
      }

      const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: uploadHeaders,
      });

      if (!uploadResponse.ok) throw new Error('Error al subir archivo');

      if (!isMultiple) {
        setUploadProgress(100);
      }

      // Step 3: Confirm upload
      const confirmResponse = await fetch(`${apiBaseUrl}/attachments/facturas/${encodeURIComponent(facturaId)}/${attachment_id}/confirm`, {
        method: 'POST',
      });

      if (!confirmResponse.ok) throw new Error('Error al confirmar carga');
      const confirmData = await confirmResponse.json();

      // Extraer attachment de la respuesta
      const newAttachment = confirmData.data || confirmData;

      setAttachments(prev => {
        const updated = [...prev, newAttachment];
        // Ordenar alfabéticamente
        updated.sort((a, b) => a.filename.localeCompare(b.filename, 'es', { sensitivity: 'base' }));
        return updated;
      });
      onAttachmentAdded?.(newAttachment);

      // Remove from uploading list
      setUploadingFiles(prev => prev.filter(name => name !== file.name));

      if (!isMultiple) {
        setTimeout(() => {
          setUploadProgress(null);
        }, 1000);
      }

      return true;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error al subir archivo';
      setError(`${file.name}: ${errorMsg}`);
      onError?.(`${file.name}: ${errorMsg}`);

      // Remove from uploading list
      setUploadingFiles(prev => prev.filter(name => name !== file.name));

      if (!isMultiple) {
        setUploadProgress(null);
      }

      return false;
    } finally {
      if (!isMultiple) {
        setIsLoading(false);
      }
    }
  };

  const uploadFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);

    // Validate total count
    const totalAfterUpload = attachments.length + fileArray.length;
    if (totalAfterUpload > maxAttachments) {
      const errorMsg = `Solo podés subir ${maxAttachments - attachments.length} archivo(s) más. Máximo: ${maxAttachments}`;
      setError(errorMsg);
      onError?.(errorMsg);
      return;
    }

    // Detectar conflictos de nombres
    const conflicts: Array<{ file: File; existing: Attachment }> = [];
    const noConflictFiles: File[] = [];

    for (const file of fileArray) {
      const existingFile = attachments.find(att => att.filename === file.name);
      if (existingFile) {
        conflicts.push({ file, existing: existingFile });
      } else {
        noConflictFiles.push(file);
      }
    }

    // Si hay conflictos, mostrar diálogo
    if (conflicts.length > 0) {
      setFileConflicts(conflicts);
      setPendingFiles(noConflictFiles);
      return;
    }

    // Si no hay conflictos, subir directamente
    await processFileUploads(noConflictFiles);
  };

  const processFileUploads = async (filesToUpload: File[]) => {
    if (filesToUpload.length === 0) return;

    setIsLoading(true);
    setError(null);

    let successCount = 0;
    let failCount = 0;

    // Upload files sequentially to avoid overwhelming the server
    for (const file of filesToUpload) {
      const success = await uploadFile(file, true);
      if (success) {
        successCount++;
      } else {
        failCount++;
      }
    }

    setIsLoading(false);

    // Show summary message
    if (successCount > 0 && failCount === 0) {
      // All succeeded - no need for message, files are visible
    } else if (failCount > 0) {
      const msg = successCount > 0
        ? `${successCount} archivo(s) subido(s), ${failCount} fallaron`
        : `Error al subir ${failCount} archivo(s)`;
      setError((error) => error + ' - ' + msg);
      onError?.(msg);
    }
  };

  // Funciones para manejar conflictos de archivos
  const getUniqueFilename = (originalName: string): string => {
    const nameParts = originalName.split('.');
    const extension = nameParts.length > 1 ? nameParts.pop() : '';
    const baseName = nameParts.join('.');
    
    let counter = 1;
    let newName = originalName;
    
    while (attachments.find(att => att.filename === newName)) {
      newName = extension 
        ? `${baseName} (${counter}).${extension}`
        : `${baseName} (${counter})`;
      counter++;
    }
    
    return newName;
  };

  const handleConflictReplace = async (conflict: { file: File; existing: Attachment }) => {
    // Primero eliminar el archivo existente
    try {
      await handleDelete(conflict.existing.attachment_id);
      // Luego subir el nuevo archivo
      await uploadFile(conflict.file, true);
    } catch (err) {
      const errorMsg = `Error al reemplazar ${conflict.file.name}`;
      setError(errorMsg);
      onError?.(errorMsg);
    }
  };

  const handleConflictKeepBoth = async (conflict: { file: File; existing: Attachment }) => {
    // Renombrar el archivo nuevo
    const newName = getUniqueFilename(conflict.file.name);
    const renamedFile = new File([conflict.file], newName, { type: conflict.file.type });
    await uploadFile(renamedFile, true);
  };

  const handleConflictDiscard = (conflict: { file: File; existing: Attachment }) => {
    // No hacer nada, simplemente descartar
  };

  const resolveConflict = async (index: number, action: 'replace' | 'keep' | 'discard') => {
    const conflict = fileConflicts[index];
    
    if (action === 'replace') {
      await handleConflictReplace(conflict);
    } else if (action === 'keep') {
      await handleConflictKeepBoth(conflict);
    }
    // 'discard' no necesita acción
    
    // Remover el conflicto resuelto
    const remainingConflicts = fileConflicts.filter((_, i) => i !== index);
    setFileConflicts(remainingConflicts);
    
    // Si era el último conflicto, subir los archivos pendientes
    if (remainingConflicts.length === 0 && pendingFiles.length > 0) {
      await processFileUploads(pendingFiles);
      setPendingFiles([]);
    }
  };

  const resolveAllConflicts = async (action: 'replace' | 'keep' | 'discard') => {
    setIsLoading(true);
    
    for (const conflict of fileConflicts) {
      if (action === 'replace') {
        await handleConflictReplace(conflict);
      } else if (action === 'keep') {
        await handleConflictKeepBoth(conflict);
      }
    }
    
    // Subir archivos sin conflicto
    await processFileUploads(pendingFiles);
    
    // Limpiar estados
    setFileConflicts([]);
    setPendingFiles([]);
    setIsLoading(false);
  };

  const cancelConflictResolution = () => {
    setFileConflicts([]);
    setPendingFiles([]);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Siempre usar uploadFiles para manejar conflictos, incluso con un solo archivo
      uploadFiles(files);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Siempre usar uploadFiles para manejar conflictos, incluso con un solo archivo
      uploadFiles(files);
    }
  };

  // Drag & Drop handlers for button
  const handleButtonDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingButton(true);
  };

  const handleButtonDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleButtonDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingButton(false);
  };

  const handleButtonDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingButton(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Siempre usar uploadFiles para manejar conflictos, incluso con un solo archivo
      uploadFiles(files);
    }
  };

  const handleDelete = async (attachmentId: string) => {
    try {
      const response = await fetch(`${apiBaseUrl}/attachments/facturas/${encodeURIComponent(facturaId)}/${attachmentId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Error al eliminar adjunto');

      setAttachments(attachments.filter(a => a.attachment_id !== attachmentId));
      onAttachmentDeleted?.(attachmentId);
      setDeleteConfirmId(null);
    } catch (err) {
      const errorMsg = 'No se pudo eliminar el adjunto';
      setError(errorMsg);
      onError?.(errorMsg);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const response = await fetch(`${apiBaseUrl}/attachments/facturas/${encodeURIComponent(facturaId)}/${attachment.attachment_id}/download-url`);
      if (!response.ok) throw new Error('Error al obtener URL de descarga');
      const responseData = await response.json();

      // Extraer download_url de la respuesta (maneja tanto {data: {download_url: ...}} como {download_url: ...} directo)
      const data = responseData.data || responseData;
      const { download_url } = data;

      // Fetch the file as blob
      const fileResponse = await fetch(download_url);
      if (!fileResponse.ok) throw new Error('Error al descargar archivo');
      const blob = await fileResponse.blob();

      // Create blob URL and trigger download
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (err) {
      const errorMsg = 'No se pudo obtener el enlace de descarga';
      setError(errorMsg);
      onError?.(errorMsg);
    }
  };

  const handleOpenFile = async (attachment: Attachment) => {
    try {
      const response = await fetch(`${apiBaseUrl}/attachments/facturas/${encodeURIComponent(facturaId)}/${attachment.attachment_id}/download-url`);
      if (!response.ok) throw new Error('Error al obtener URL de descarga');
      const responseData = await response.json();

      // Extraer download_url de la respuesta
      const data = responseData.data || responseData;
      const { download_url } = data;

      // Mostrar en popup viewer en lugar de abrir en nueva pestaña
      setViewerFile({ attachment, url: download_url });

      // Si es archivo de texto, cargar su contenido
      if (isTextFile(attachment.filename)) {
        setLoadingText(true);
        try {
          const textResponse = await fetch(download_url);
          if (textResponse.ok) {
            const text = await textResponse.text();
            setTextContent(text);
          }
        } catch (err) {
          console.error('Error loading text content:', err);
          setTextContent('Error al cargar el contenido del archivo');
        } finally {
          setLoadingText(false);
        }
      } else {
        setTextContent(null);
      }
    } catch (err) {
      const errorMsg = 'No se pudo abrir el archivo';
      setError(errorMsg);
      onError?.(errorMsg);
    }
  };

  // Selection handlers
  const toggleFileSelection = (attachmentId: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(attachmentId)) {
        newSet.delete(attachmentId);
      } else {
        newSet.add(attachmentId);
      }
      return newSet;
    });
  };

  const selectAllFiles = () => {
    setSelectedFiles(new Set(attachments.map(a => a.attachment_id)));
  };

  const clearSelection = () => {
    setSelectedFiles(new Set());
  };

  // Bulk actions
  const downloadSelectedFiles = async () => {
    for (const attachmentId of selectedFiles) {
      const attachment = attachments.find(a => a.attachment_id === attachmentId);
      if (attachment) {
        await handleDownload(attachment);
        // Pequeña pausa entre descargas para evitar bloqueo
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  };

  const deleteSelectedFiles = () => {
    setDeleteConfirmId('bulk');
  };

  const confirmBulkDelete = async () => {
    setIsLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const attachmentId of selectedFiles) {
      try {
        const response = await fetch(`${apiBaseUrl}/attachments/facturas/${encodeURIComponent(facturaId)}/${attachmentId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          successCount++;
          onAttachmentDeleted?.(attachmentId);
        } else {
          failCount++;
        }
      } catch (err) {
        failCount++;
      }
    }

    setAttachments(prev => prev.filter(a => !selectedFiles.has(a.attachment_id)));
    setSelectedFiles(new Set());
    setDeleteConfirmId(null);
    setIsLoading(false);

    if (successCount > 0) {
      const msg = failCount > 0
        ? `${successCount} archivo(s) eliminado(s), ${failCount} fallaron`
        : `${successCount} archivo(s) eliminado(s) correctamente`;
      if (failCount > 0) {
        setError(msg);
        onError?.(msg);
      }
    }
  };

  const handleFileClick = (attachment: Attachment, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd + clic = alternar selección
      toggleFileSelection(attachment.attachment_id);
    } else {
      // Clic normal = abrir archivo en una pestaña nueva
      handleOpenFile(attachment);
    }
  };

  const handleSelectionIndicatorClick = (attachmentId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent file click event
    toggleFileSelection(attachmentId);
  };

  // Viewer helper functions
  const isOfficeDocument = (filename: string): boolean => {
    const ext = filename.toLowerCase().split('.').pop();
    // CSV se muestra como texto plano, no como documento de Office
    return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].indexOf(ext || '') !== -1;
  };

  const isImage = (filename: string): boolean => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].indexOf(ext || '') !== -1;
  };

  const isTextFile = (filename: string): boolean => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['txt', 'csv', 'md', 'json', 'xml', 'log'].indexOf(ext || '') !== -1;
  };

  const getViewerUrl = (url: string, filename: string): string => {
    if (isOfficeDocument(filename)) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }
    return url;
  };

  const handleDownloadFromViewer = async () => {
    if (!viewerFile) return;
    await handleDownload(viewerFile.attachment);
  };

  const handleDeleteFromViewer = async () => {
    if (!viewerFile) return;
    const attachmentId = viewerFile.attachment.attachment_id;
    setDeleteConfirmId(attachmentId);
    setViewerFile(null);
    setTextContent(null);
  };

  const closeViewer = () => {
    setViewerFile(null);
    setTextContent(null);
  };

  return (
    <>
      {/* Regular Button with File Info */}
      <div className="attachments-section">
        <button
          className={`attachments-button ${attachments.length > 0 ? 'has-attachments' : ''} ${isDraggingButton ? 'dragging' : ''}`}
          onClick={() => setIsOpen(true)}
          onDragEnter={!isLoading ? handleButtonDragEnter : undefined}
          onDragOver={!isLoading ? handleButtonDragOver : undefined}
          onDragLeave={!isLoading ? handleButtonDragLeave : undefined}
          onDrop={!isLoading ? handleButtonDrop : undefined}
        >
          {isDraggingButton && (
            <div className="button-drag-overlay">
              <div className="button-drag-icon">📁</div>
              <div className="button-drag-text">Soltá los archivos</div>
            </div>
          )}
          <div className="attachments-button-content">
            <div className="attachments-button-header">
              <span className="attachments-icon">📎</span>
              <span className="attachments-title">Archivos Adjuntos</span>
              {attachments.length > 0 && (
                <span className="attachments-count">{attachments.length}</span>
              )}
            </div>
            {attachments.length > 0 ? (
              <div className="attachments-preview">
                {attachments.slice(0, 3).map((att) => (
                  <div key={att.attachment_id} className="attachment-preview-item">
                    <span className="preview-icon">{getFileIcon(att.filename)}</span>
                    <span className="preview-name">{att.filename}</span>
                    <span className="preview-size">{formatFileSize(att.file_size)}</span>
                  </div>
                ))}
                {attachments.length > 3 && (
                  <div className="attachment-preview-more">
                    +{attachments.length - 3} más
                  </div>
                )}
              </div>
            ) : (
              <div className="attachments-empty-state">
                <span className="empty-text">No hay archivos adjuntos</span>
                <span className="drag-hint-text">
                  <span className="drag-hint-icon-small">🖱️</span>
                  Arrastrá archivos aquí o hacé click para abrir
                </span>
              </div>
            )}
          </div>
        </button>
      </div>

      {/* Popup Modal */}
      {isOpen && (
        <div className="attachments-overlay">
          <div className="attachments-popup">
            {/* Header */}
            <div className="attachments-header">
              <div>
                <h3>📎 Archivos Adjuntos</h3>

              </div>
              <button className="attachments-close" onClick={() => setIsOpen(false)}>
                ✕
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="attachments-error">
                <span>⚠️</span>
                <span>{error}</span>
                <button onClick={() => setError(null)}>✕</button>
              </div>
            )}

            {/* Uploading Files List */}
            {uploadingFiles.length > 0 && (
              <div className="uploading-files">
                <h4>Subiendo archivos...</h4>
                <ul>
                  {uploadingFiles.map((filename, index) => (
                    <li key={index}>
                      <span className="uploading-icon">⏳</span>
                      <span className="uploading-name">{filename}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Upload Progress */}
            {uploadProgress !== null && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            )}

            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={allowedFileTypes.map(ext => `.${ext}`).join(',')}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              disabled={isLoading}
            />

            {/* Selection Actions Bar */}
            {selectedFiles.size > 0 && (
              <div className="selection-actions-bar">
                <div className="selection-info">
                  <span className="selection-count">{selectedFiles.size} archivo(s) seleccionado(s)</span>
                  <button className="selection-clear" onClick={clearSelection}>
                    Limpiar selección
                  </button>
                </div>
                <div className="selection-actions">
                  <button
                    className="selection-action download-action"
                    onClick={downloadSelectedFiles}
                    title="Descargar seleccionados"
                  >
                    <span className="action-icon">⬇️</span>
                    Descargar
                  </button>
                  <button
                    className="selection-action delete-action"
                    onClick={deleteSelectedFiles}
                    title="Eliminar seleccionados"
                  >
                    <span className="action-icon">🗑️</span>
                    Eliminar
                  </button>
                </div>
              </div>
            )}

            {/* Attachments Grid with Drag & Drop */}
            <div
              className={`attachments-grid ${isDragging ? 'dragging' : ''} ${isLoading ? 'disabled' : ''}`}
              onDragEnter={!isLoading ? handleDragEnter : undefined}
              onDragOver={!isLoading ? handleDragOver : undefined}
              onDragLeave={!isLoading ? handleDragLeave : undefined}
              onDrop={!isLoading ? handleDrop : undefined}
            >
              {/* Drag Overlay */}
              {isDragging && (
                <div className="drag-overlay">
                  <div className="drag-overlay-content">
                    <div className="drag-icon">📁</div>
                    <p className="drag-text">Soltá los archivos aquí</p>
                  </div>
                </div>
              )}

              {attachments.length === 0 && !isLoading && (
                <div className="attachments-empty">
                  <div className="empty-icon">📭</div>
                  <p>No hay archivos adjuntos</p>
                  <p className="empty-hint">
                    <span className="drag-hint-icon">🖱️</span>
                    Arrastrá archivos aquí o usá el botón "Añadir archivos"
                  </p>
                  <button
                    className="add-files-button add-files-button-center"
                    onClick={() => !isLoading && fileInputRef.current?.click()}
                    disabled={isLoading}
                  >
                    <span className="add-icon">➕</span>
                    Añadir archivos
                  </button>
                </div>
              )}

              {attachments.length > 0 && (
                <div className="grid-header">
                  <div className="grid-header-left">
                    <button
                      className="select-all-btn"
                      onClick={selectedFiles.size === attachments.length ? clearSelection : selectAllFiles}
                    >
                      <span className="checkbox">
                        {selectedFiles.size === attachments.length ? '☑️' : '☐'}
                      </span>
                      {selectedFiles.size === attachments.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                    </button>
                    <button
                      className="add-files-button"
                      onClick={() => !isLoading && fileInputRef.current?.click()}
                      disabled={isLoading}
                    >
                      <span className="add-icon">➕</span>
                      Añadir archivos
                    </button>
                  </div>
                  <div className="grid-header-right">
                    <div className="attachments-subtitle">
                      <span className="subtitle-count">
                        {attachments.length} {attachments.length === 1 ? 'archivo' : 'archivos'}
                        {attachments.length < maxAttachments && (
                          <span className="subtitle-available"> • {maxAttachments - attachments.length} disponible{maxAttachments - attachments.length !== 1 ? 's' : ''}</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid-items drag-drop-hint">
                <div className="drag-drop-placeholder">
                  <span className="subtitle-instructions">
                    Arrastra los archivos aquí
                    <span className="subtitle-separator">•</span>
                    <span className="instruction-item">
                      <span className="instruction-icon">👆</span>
                      Click para abrir
                    </span>
                    <span className="subtitle-separator">•</span>
                    <span className="instruction-item">
                      <kbd className="kbd-key">Ctrl</kbd>&nbsp;+
                      <span className="instruction-icon">👆</span>
                      Click para seleccionar
                    </span>
                  </span>
                </div>
                {attachments.map((attachment) => (
                  <div
                    key={attachment.attachment_id}
                    className={`attachment-grid-item ${selectedFiles.has(attachment.attachment_id) ? 'selected' : ''}`}
                    onClick={(e) => handleFileClick(attachment, e)}
                  >
                    {selectedFiles.has(attachment.attachment_id) && (
                      <div
                        className="selection-indicator"
                        onClick={(e) => handleSelectionIndicatorClick(attachment.attachment_id, e)}
                        title="Click para deseleccionar"
                      >
                        ✓
                      </div>
                    )}
                    <div className="grid-item-icon">
                      {getFileIcon(attachment.filename)}
                    </div>
                    <div className="grid-item-info">
                      <div className="grid-item-name" title={attachment.filename}>
                        {attachment.filename}
                      </div>
                      <div className="grid-item-meta">
                        {formatFileSize(attachment.file_size)}
                      </div>
                    </div>
                  </div>
                ))}

              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Popup */}
      {viewerFile && (
        <div className="confirm-overlay" onClick={closeViewer}>
          <div className="file-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-header">
              <h3>📄 {viewerFile.attachment.filename}</h3>
              <button className="confirm-close" onClick={closeViewer}>
                ✕
              </button>
            </div>
            <div className={`file-viewer-content ${isImage(viewerFile.attachment.filename) ? 'image-viewer' : isTextFile(viewerFile.attachment.filename) ? 'text-viewer' : ''}`}>
              {isOfficeDocument(viewerFile.attachment.filename) ? (
                <iframe
                  src={getViewerUrl(viewerFile.url, viewerFile.attachment.filename)}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title={viewerFile.attachment.filename}
                />
              ) : isImage(viewerFile.attachment.filename) ? (
                <img
                  src={viewerFile.url}
                  alt={viewerFile.attachment.filename}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : isTextFile(viewerFile.attachment.filename) ? (
                <div className="text-content-wrapper">
                  {loadingText ? (
                    <div className="text-loading">Cargando contenido...</div>
                  ) : (
                    <pre className="text-content">{textContent || 'No se pudo cargar el contenido'}</pre>
                  )}
                </div>
              ) : (
                <iframe
                  src={viewerFile.url}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title={viewerFile.attachment.filename}
                />
              )}
            </div>
            <div className="file-viewer-footer">
              <button
                className="viewer-download-button"
                onClick={handleDownloadFromViewer}
              >
                ⬇️ Descargar
              </button>
              <button
                className="viewer-delete-button"
                onClick={handleDeleteFromViewer}
              >
                🗑️ Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Conflicts Popup */}
      {fileConflicts.length > 0 && (
        <div className="confirm-overlay" onClick={cancelConflictResolution}>
          <div className="conflicts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-header">
              <h3>⚠️ Archivos con nombres duplicados</h3>
              <button className="confirm-close" onClick={cancelConflictResolution}>
                ✕
              </button>
            </div>
            <div className="conflicts-content">
              <p className="conflicts-intro">
                {fileConflicts.length === 1
                  ? 'Ya existe un archivo con este nombre. ¿Qué querés hacer?'
                  : `Se encontraron ${fileConflicts.length} archivos con nombres duplicados. Elegí una opción para cada uno:`
                }
              </p>
              
              <div className="conflicts-list">
                {fileConflicts.map((conflict, index) => (
                  <div key={index} className="conflict-item">
                    <div className="conflict-file-info">
                      <div className="conflict-icon">📄</div>
                      <div className="conflict-details">
                        <div className="conflict-filename">{conflict.file.name}</div>
                        <div className="conflict-meta">
                          <span>Nuevo: {formatFileSize(conflict.file.size)}</span>
                          <span className="conflict-separator">•</span>
                          <span>Existente: {formatFileSize(conflict.existing.file_size)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="conflict-actions">
                      <button
                        className="conflict-btn replace-btn"
                        onClick={() => resolveConflict(index, 'replace')}
                        title="Eliminar el archivo existente y subir el nuevo"
                      >
                        🔄 Reemplazar
                      </button>
                      <button
                        className="conflict-btn keep-btn"
                        onClick={() => resolveConflict(index, 'keep')}
                        title="Renombrar el archivo nuevo y mantener ambos"
                      >
                        📑 Mantener ambos
                      </button>
                      <button
                        className="conflict-btn discard-btn"
                        onClick={() => resolveConflict(index, 'discard')}
                        title="No subir el archivo nuevo"
                      >
                        ❌ Descartar
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {fileConflicts.length > 1 && (
                <div className="conflicts-bulk-actions">
                  <p className="bulk-actions-label">Aplicar a todos:</p>
                  <div className="bulk-actions-buttons">
                    <button
                      className="conflict-btn replace-btn"
                      onClick={() => resolveAllConflicts('replace')}
                    >
                      🔄 Reemplazar todos
                    </button>
                    <button
                      className="conflict-btn keep-btn"
                      onClick={() => resolveAllConflicts('keep')}
                    >
                      📑 Mantener todos
                    </button>
                    <button
                      className="conflict-btn discard-btn"
                      onClick={() => resolveAllConflicts('discard')}
                    >
                      ❌ Descartar todos
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="confirm-footer">
              <button
                className="confirm-cancel-button"
                onClick={cancelConflictResolution}
              >
                Cancelar todo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {deleteConfirmId && (
        <div className="confirm-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="confirm-popup" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-header">
              <h3>⚠️ Eliminar {deleteConfirmId === 'bulk' ? 'Archivos' : 'Adjunto'}</h3>
              <button className="confirm-close" onClick={() => setDeleteConfirmId(null)}>
                ✕
              </button>
            </div>
            <div className="confirm-content">
              <div className="confirm-icon">🗑️</div>
              <p className="confirm-message">
                {deleteConfirmId === 'bulk'
                  ? `¿Estás seguro de que querés eliminar ${selectedFiles.size} archivo(s)?`
                  : '¿Estás seguro de que querés eliminar este archivo?'
                }
              </p>
              <p className="confirm-hint">Esta acción no se puede deshacer.</p>
            </div>
            <div className="confirm-footer">
              <button
                className="confirm-cancel-button"
                onClick={() => setDeleteConfirmId(null)}
              >
                Cancelar
              </button>
              <button
                className="confirm-ok-button confirm-delete-button"
                onClick={() => deleteConfirmId === 'bulk' ? confirmBulkDelete() : handleDelete(deleteConfirmId)}
              >
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FacturasAttachments;
